import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  RefreshCw,
  Users,
  IndianRupee,
  Clock,
  Play,
  Pause,
  XCircle,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Image,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/Dialog';
import { formatCurrency, formatRelativeTime, getInitials } from '@/lib/utils';
import { gamesApi } from '@/lib/adminApi';

interface LudoRoom {
  id: string;
  roomCode: string;
  ludoKingRoomCode?: string;
  entryFee: number;
  prizePool: number;
  status: 'waiting' | 'playing' | 'completed' | 'cancelled' | 'resultPending' | 'expired';
  players: {
    id: string;
    name: string;
    color: 'red' | 'green' | 'yellow' | 'blue';
    isReady: boolean;
  }[];
  maxPlayers: number;
  startedAt?: string;
  createdAt: string;
  playerSubmissions?: Array<{
    userId: { _id: string; name?: string; username?: string };
    screenshotUrl: string;
    submittedAt: string;
    claimedWinner: boolean;
  }>;
  verificationStatus?: 'pending' | 'approved' | 'rejected';
  adminNotes?: string;
}

const colorOptions: ('red' | 'green' | 'yellow' | 'blue')[] = ['red', 'green', 'yellow', 'blue'];

const colors: Record<string, string> = {
  red: 'bg-red-500',
  green: 'bg-emerald-500',
  yellow: 'bg-amber-500',
  blue: 'bg-blue-500',
};

export default function LudoRooms() {
  const [rooms, setRooms] = useState<LudoRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRoom, setSelectedRoom] = useState<LudoRoom | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [verifying, setVerifying] = useState(false);
  const [rejectionNotes, setRejectionNotes] = useState('');
  const [selectedWinners, setSelectedWinners] = useState<string[]>([]);
  const itemsPerPage = 12;

  const fetchRooms = useCallback(async () => {
    try {
      setError(null);
      const params: Record<string, string | number> = {
        page: currentPage,
        limit: itemsPerPage,
        gameType: 'ludo',
      };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (search) {
        params.search = search;
      }

      const response = await gamesApi.getGames(params);
      const data = response.data;

      if (data.success && data.data) {
        const mappedRooms: LudoRoom[] = (data.data.games || []).map((game: any) => ({
          id: game._id || game.id,
          roomCode: game.ludoKingRoomCode || game.roomNumber || 'N/A',
          entryFee: game.entryFee || 0,
          prizePool: game.winningAmount || 0,
          status: game.status,
          players: (game.players || []).map((p: any, pIndex: number) => ({
            id: p.user?._id || p.user || `player_${pIndex}`,
            name: p.user?.name || p.user?.username || 'Player',
            color: colorOptions[pIndex % 4],
            isReady: p.isReady || false,
          })),
          maxPlayers: game.roomType === '4-player' ? 4 : 2,
          startedAt: game.startedAt,
          createdAt: game.createdAt,
          ludoKingRoomCode: game.ludoKingRoomCode,
          playerSubmissions: game.playerSubmissions || [],
          verificationStatus: game.verificationStatus,
          adminNotes: game.adminNotes,
        }));

        setRooms(mappedRooms);
        setPagination({
          total: data.data.pagination?.total || mappedRooms.length,
          pages: data.data.pagination?.pages || 1,
        });
      }
    } catch (err: any) {
      console.error('Failed to fetch rooms:', err);
      setError(err.message || 'Failed to load game rooms');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [currentPage, statusFilter, search]);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchRooms();
  };

  const handleVerification = async (action: 'approve' | 'reject') => {
    if (!selectedRoom) return;

    if (action === 'reject' && !rejectionNotes.trim()) {
      alert('Please provide rejection notes');
      return;
    }

    try {
      setVerifying(true);
      await gamesApi.verifyGameResult(selectedRoom.id, {
        action,
        notes: rejectionNotes || undefined,
        winnerIds: action === 'approve' ? selectedWinners : undefined,
      });

      // Refresh rooms list and close modal
      await fetchRooms();
      setSelectedRoom(null);
      setRejectionNotes('');
      setSelectedWinners([]);
    } catch (err: any) {
      console.error('Failed to verify game:', err);
      alert(err.response?.data?.message || 'Failed to verify game result');
    } finally {
      setVerifying(false);
    }
  };

  const filteredRooms = rooms.filter((room) => {
    const matchesSearch = room.roomCode.toLowerCase().includes(search.toLowerCase());
    return matchesSearch;
  });

  const totalPages = pagination.pages || Math.ceil(filteredRooms.length / itemsPerPage);
  const paginatedRooms = filteredRooms;

  const activeRooms = rooms.filter((r) => r.status === 'playing').length;
  const waitingRooms = rooms.filter((r) => r.status === 'waiting').length;
  const totalPrizePool = rooms
    .filter((r) => r.status === 'playing')
    .reduce((sum, r) => sum + r.prizePool, 0);
  const playersOnline = rooms
    .filter((r) => r.status === 'playing')
    .reduce((sum, r) => sum + r.players.length, 0);

  if (loading && rooms.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-violet-400" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold flex items-center gap-3">
            <span className="text-4xl">🎲</span>
            Ludo Rooms
          </h1>
          <p className="text-slate-400 mt-1">Monitor active Ludo game rooms in real-time</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <Card variant="glass" className="p-4 border-red-500/50">
          <div className="flex items-center gap-3 text-red-400">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card variant="glass" className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <Play className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-400">{activeRooms}</p>
              <p className="text-sm text-slate-400">Active Games</p>
            </div>
          </div>
        </Card>

        <Card variant="glass" className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-400">{waitingRooms}</p>
              <p className="text-sm text-slate-400">Waiting Rooms</p>
            </div>
          </div>
        </Card>

        <Card variant="glass" className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
              <IndianRupee className="w-5 h-5 text-violet-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-violet-400">{formatCurrency(totalPrizePool)}</p>
              <p className="text-sm text-slate-400">Active Prize Pool</p>
            </div>
          </div>
        </Card>

        <Card variant="glass" className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-400">{playersOnline}</p>
              <p className="text-sm text-slate-400">Players Online</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card variant="glass" className="p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search by room code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              icon={<Search className="w-4 h-4" />}
            />
          </div>
          <div className="flex gap-3">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 rounded-lg bg-slate-900/50 border border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
            >
              <option value="all">All Status</option>
              <option value="waiting">Waiting</option>
              <option value="playing">Playing</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Rooms Grid */}
      {paginatedRooms.length === 0 ? (
        <Card variant="glass" className="p-8 text-center">
          <p className="text-slate-400">No Ludo rooms found</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <AnimatePresence mode="popLayout">
            {paginatedRooms.map((room, index) => (
              <motion.div
                key={room.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card
                  variant="glass"
                  className={`p-4 cursor-pointer transition-all hover:border-violet-500/50 ${room.status === 'playing' ? 'border-emerald-500/30' : ''
                    }`}
                  onClick={() => setSelectedRoom(room)}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="font-mono font-bold text-lg">{room.roomCode}</p>
                      <p className="text-xs text-slate-500">
                        {room.status === 'playing' && room.startedAt
                          ? `Started ${formatRelativeTime(room.startedAt)}`
                          : `Created ${formatRelativeTime(room.createdAt)}`}
                      </p>
                    </div>
                    <Badge
                      variant={
                        room.status === 'playing'
                          ? 'success'
                          : room.status === 'waiting'
                            ? 'warning'
                            : 'default'
                      }
                    >
                      {room.status === 'playing' && (
                        <span className="w-2 h-2 rounded-full bg-emerald-400 mr-2 animate-pulse" />
                      )}
                      {room.status.replace('_', ' ')}
                    </Badge>
                  </div>

                  {/* Players */}
                  <div className="flex items-center gap-2 mb-4">
                    {room.players.map((player) => (
                      <div
                        key={player.id}
                        className={`w-8 h-8 rounded-full ${colors[player.color] || 'bg-slate-500'} flex items-center justify-center text-white text-xs font-bold ring-2 ring-slate-900`}
                        title={player.name}
                      >
                        {getInitials(player.name)}
                      </div>
                    ))}
                    {Array.from({ length: room.maxPlayers - room.players.length }).map((_, i) => (
                      <div
                        key={i}
                        className="w-8 h-8 rounded-full bg-slate-700 border-2 border-dashed border-slate-600 flex items-center justify-center text-slate-500"
                      >
                        ?
                      </div>
                    ))}
                  </div>

                  {/* Info */}
                  <div className="grid grid-cols-2 gap-2 text-center">
                    <div className="p-2 bg-slate-800/50 rounded">
                      <p className="text-sm font-semibold">{formatCurrency(room.entryFee)}</p>
                      <p className="text-xs text-slate-500">Entry</p>
                    </div>
                    <div className="p-2 bg-slate-800/50 rounded">
                      <p className="text-sm font-semibold text-emerald-400">
                        {formatCurrency(room.prizePool)}
                      </p>
                      <p className="text-xs text-slate-500">Prize</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Pagination */}
      {pagination.total > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-400">
            Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
            {Math.min(currentPage * itemsPerPage, pagination.total)} of {pagination.total}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Room Detail Modal */}
      <Dialog open={!!selectedRoom} onOpenChange={() => setSelectedRoom(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span className="text-2xl">🎲</span>
              Room: {selectedRoom?.roomCode}
            </DialogTitle>
            <DialogDescription>Game room details and player information</DialogDescription>
          </DialogHeader>
          {selectedRoom && (
            <div className="py-4 space-y-6 max-h-[70vh] overflow-y-auto pr-2">
              {/* Ludo King Room Code */}
              {selectedRoom.ludoKingRoomCode && (
                <div className="p-4 bg-slate-800/30 rounded-lg">
                  <p className="text-sm text-slate-400 mb-1">Ludo King Room Code</p>
                  <p className="text-2xl font-mono font-bold text-violet-400">
                    {selectedRoom.ludoKingRoomCode}
                  </p>
                </div>
              )}

              {/* Game Status */}
              <div className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg">
                <div>
                  <p className="text-sm text-slate-400">Status</p>
                  <Badge
                    variant={
                      selectedRoom.status === 'playing'
                        ? 'success'
                        : selectedRoom.status === 'waiting'
                          ? 'warning'
                          : 'default'
                    }
                    className="mt-1"
                  >
                    {selectedRoom.status.replace('_', ' ')}
                  </Badge>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-400">Prize Pool</p>
                  <p className="text-2xl font-bold text-emerald-400">
                    {formatCurrency(selectedRoom.prizePool)}
                  </p>
                </div>
              </div>

              {/* Players */}
              <div>
                <h4 className="font-semibold mb-3">Players</h4>
                <div className="grid grid-cols-2 gap-3">
                  {selectedRoom.players.map((player) => (
                    <div
                      key={player.id}
                      className="p-3 rounded-lg border border-slate-700 bg-slate-800/30"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-full ${colors[player.color] || 'bg-slate-500'} flex items-center justify-center text-white font-bold`}
                        >
                          {getInitials(player.name)}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{player.name}</p>
                          <p className="text-sm text-slate-400 capitalize">{player.color}</p>
                        </div>
                        <div className="text-right">
                          <Badge variant={player.isReady ? 'success' : 'warning'}>
                            {player.isReady ? 'Ready' : 'Not Ready'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Game Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-slate-800/30 rounded-lg">
                  <p className="text-2xl font-bold">{formatCurrency(selectedRoom.entryFee)}</p>
                  <p className="text-sm text-slate-400">Entry Fee</p>
                </div>
                <div className="text-center p-3 bg-slate-800/30 rounded-lg">
                  <p className="text-2xl font-bold">{selectedRoom.players.length}/{selectedRoom.maxPlayers}</p>
                  <p className="text-sm text-slate-400">Players</p>
                </div>
              </div>

              {/* Actions */}
              {selectedRoom.status === 'playing' && (
                <div className="flex gap-3">
                  <Button variant="outline" className="flex-1">
                    <Pause className="w-4 h-4 mr-2" />
                    Pause Game
                  </Button>
                  <Button variant="destructive" className="flex-1">
                    <XCircle className="w-4 h-4 mr-2" />
                    Force End
                  </Button>
                </div>
              )}

              {/* Player Submissions for resultPending */}
              {selectedRoom.status === 'resultPending' && (
                <div className="space-y-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Player Submissions ({selectedRoom.playerSubmissions?.length || 0})
                  </h4>

                  {selectedRoom.playerSubmissions && selectedRoom.playerSubmissions.length > 0 ? (
                    <div className="space-y-4">
                      {selectedRoom.playerSubmissions.map((submission) => {
                        // Add null safety check
                        if (!submission.userId || !submission.userId._id) {
                          console.error('Invalid submission data:', submission);
                          return null;
                        }

                        const userId = submission.userId._id;
                        const userName = submission.userId.name || submission.userId.username || 'Player';

                        return (
                          <div key={userId} className="p-4 bg-slate-800/30 rounded-lg border border-slate-700">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-3">
                                <input
                                  type="checkbox"
                                  id={`winner-${userId}`}
                                  checked={selectedWinners.includes(userId)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedWinners([...selectedWinners, userId]);
                                    } else {
                                      setSelectedWinners(selectedWinners.filter(id => id !== userId));
                                    }
                                  }}
                                  className="w-4 h-4 rounded border-slate-600 text-violet-600 focus:ring-violet-500 focus:ring-offset-slate-900"
                                />
                                <label htmlFor={`winner-${userId}`} className="font-medium cursor-pointer">
                                  {userName}
                                </label>
                              </div>
                              {submission.claimedWinner && (
                                <Badge variant="success">Claims Winner</Badge>
                              )}
                            </div>
                            <img
                              src={submission.screenshotUrl}
                              alt={`Screenshot by ${submission.userId.name}`}
                              className="w-full h-64 object-contain rounded-lg border border-slate-700 bg-slate-900 cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => window.open(submission.screenshotUrl, '_blank')}
                            />
                            <p className="text-xs text-slate-400 mt-2">
                              Submitted {formatRelativeTime(submission.submittedAt)}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-4 bg-slate-800/30 rounded-lg text-center text-slate-400">
                      <Image className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      No screenshots submitted yet
                    </div>
                  )}

                  {/* Rejection Notes Input */}
                  <div>
                    <label className="text-sm text-slate-400 block mb-2">
                      Notes (required for rejection)
                    </label>
                    <textarea
                      className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-violet-500 focus:outline-none"
                      rows={2}
                      placeholder="Enter reason for approval/rejection..."
                      value={rejectionNotes}
                      onChange={(e) => setRejectionNotes(e.target.value)}
                    />
                  </div>

                  {/* Verification Buttons */}
                  <div className="flex gap-3">
                    <Button
                      variant="default"
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => handleVerification('approve')}
                      disabled={verifying || selectedWinners.length === 0}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {verifying ? 'Processing...' : `Pay ${selectedWinners.length} Winner${selectedWinners.length !== 1 ? 's' : ''}`}
                    </Button>
                    <Button
                      variant="destructive"
                      className="flex-1"
                      onClick={() => handleVerification('reject')}
                      disabled={verifying || !rejectionNotes.trim()}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
